/**
 * The block picker both studios add content with.
 *
 * One sheet, one contract: a drag handle, a title that says where the block is
 * going, a search field, a close button, a backdrop that dismisses, grouped rows
 * with an icon chip, and a "Soon" badge on anything the play engine cannot render
 * yet.
 *
 * The rows are supplied by the caller (see `#/lib/studioBlockPicker`), so the
 * FlashBook studio can offer its rich reading blocks as extra rows without the
 * Capsule studio knowing they exist — and neither has to re-implement the
 * sheet, its scroll behaviour or its accessibility. The search lives *here* rather
 * than in either caller for the same reason the rows do: the sheet is handed every
 * block a studio offers, so one field can search all of them, and a studio added
 * later gets the search without doing anything.
 *
 * Three shapes of the same rows, one question each: **search** while a creator
 * knows what they want (a flat list, ranked with what they used last first),
 * **recently used** when they don't but they just added one, and the **labelled
 * sections** when they are browsing at all. See `#/lib/studioBlockPicker` for the
 * matching, and `#/lib/studioRecentBlocks` for what is remembered.
 */
import {type ComponentProps, useState} from 'react'
import {
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {
  recentStudioPickerEntries,
  searchStudioPickerEntries,
  type StudioPickerEntry,
  type StudioPickerSection,
} from '#/lib/studioBlockPicker'
import {
  getRecentStudioBlocks,
  rememberStudioBlock,
} from '#/lib/studioRecentBlocks'
import * as Toast from '#/view/com/util/Toast'
import {Text} from '#/components/Typography'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {SLIDE_TYPE_META} from '#/types/capsule'

/**
 * The two labels no caller can supply, because they describe what the sheet is
 * doing rather than what a studio offers: a search's results, and the blocks the
 * creator reached for last.
 */
const RECENT_LABEL = msg`Recently used`
const RESULTS_LABEL = msg`Results`

export function StudioBlockPicker({
  title,
  subtitle,
  sections,
  footnote,
  onSelect,
  onClose,
  testID = 'studioBlockPicker',
}: {
  /** What the sheet is doing, e.g. "Add a block". */
  title: string
  /** Where the new block lands — the one thing a creator needs to know. */
  subtitle: string
  sections: StudioPickerSection[]
  /** A closing line for the sheet, e.g. how to reorder what was just added. */
  footnote?: string
  onSelect: (entry: StudioPickerEntry) => void
  onClose: () => void
  testID?: string
}) {
  const c = useStudioChrome()
  const ss = useStyles()
  const {t: l} = useLingui()

  /*
   * The query and the device's own recent list, both owned here so the search and
   * the shortcut stay one feature rather than something each studio has to wire
   * up. The recents are read on mount — the sheet is mounted fresh every time it
   * opens — and written on the way out, when a block is actually added.
   */
  const [query, setQuery] = useState('')
  const [recent, setRecent] = useState<string[]>(() => getRecentStudioBlocks())

  /*
   * What a row is searched by: every word a creator can see on it (translated, so
   * the search and the paint agree in the app's own language) plus its key, which
   * is what makes an underscore query like `fill_blank` reach the row whose label
   * is "Fill in the blanks".
   */
  const textOf = (entry: StudioPickerEntry) =>
    `${l(entry.label)} ${l(entry.desc)} ${entry.key}`

  const trimmed = query.trim()
  const searching = trimmed.length > 0
  const recentEntries = recentStudioPickerEntries(sections, recent)
  const results = searching
    ? searchStudioPickerEntries(sections, trimmed, textOf, recent)
    : []
  const empty = searching && results.length === 0

  /*
   * Three shapes, and never two at once. While there is a query the sheet shows
   * one flat result list and *no* group labels at all: a labelled section is
   * exactly the thing the creator is searching to avoid scrolling, and leaving the
   * other sections on screen would hand them back the list they just narrowed.
   */
  const shown: StudioPickerSection[] = searching
    ? results.length
      ? [{key: 'search', label: RESULTS_LABEL, entries: results}]
      : []
    : [
        ...(recentEntries.length
          ? [{key: 'recent', label: RECENT_LABEL, entries: recentEntries}]
          : []),
        ...sections,
      ]

  return (
    <>
      {/* Tapping the page behind the sheet dismisses it: the same gesture that
          closes every other sheet in the app, and the fastest way out. */}
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={l`Close the block picker`}
        accessibilityHint={l`Dismisses the picker`}
        activeOpacity={1}
        onPress={onClose}
        style={ss.backdrop}
      />
      <View style={ss.sheet} testID={testID}>
        <View style={ss.handle} />
        <View style={ss.header}>
          <View style={ss.headerText}>
            <Text style={ss.title}>{title}</Text>
            <Text style={ss.subtitle} numberOfLines={1}>
              {subtitle}
            </Text>
          </View>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Close`}
            accessibilityHint={l`Dismisses the picker without adding a block`}
            onPress={onClose}
            style={ss.close}>
            <Feather name="x" size={16} color={c.text} />
          </TouchableOpacity>
        </View>

        {/*
          The search sits above the scroll rather than in it: it is how a creator
          finds their way through the list, so it must not scroll away from them.
          It is deliberately *not* focused on open — the sheet's job is to show what
          is on offer, and a keyboard over the rows is the worst way to do that.
        */}
        <View style={ss.search} testID={`${testID}-searchField`}>
          <Feather name="search" size={15} color={c.textMuted} />
          <TextInput
            accessibilityLabel={l`Search blocks`}
            accessibilityHint={l`Finds a block by name across everything this studio offers`}
            value={query}
            onChangeText={setQuery}
            placeholder={l`Search all blocks`}
            placeholderTextColor={c.textFaint}
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="search"
            testID={`${testID}-search`}
            style={ss.searchInput}
          />
          {query.length > 0 && (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Clear the search`}
              accessibilityHint={l`Shows the labelled sections again`}
              onPress={() => setQuery('')}
              testID={`${testID}-searchClear`}
              style={ss.searchClear}>
              <Feather name="x" size={13} color={c.text} />
            </TouchableOpacity>
          )}
        </View>

        <ScrollView
          style={{flex: 1}}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={ss.scroll}
          keyboardShouldPersistTaps="handled">
          {shown.map(section => (
            <View
              key={section.key}
              style={ss.group}
              testID={`${testID}-section-${section.key}`}>
              <Text style={ss.groupLabel}>{l(section.label)}</Text>
              {section.entries.map(entry => {
                // The play engine decides whether a type can be answered yet;
                // the picker offers it either way, so a creator can see what is
                // coming instead of wondering why the list is short.
                const supported =
                  SLIDE_TYPE_META[entry.type]?.supported !== false
                // The taxonomy colour is drawn as ink and tint on this sheet, so
                // it is tuned for the sheet's surface first.
                const entryColor = readableAccent(entry.color, c)
                return (
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={l(entry.label)}
                    accessibilityHint={
                      supported
                        ? l`Adds this block`
                        : l`Not playable yet — it is coming soon`
                    }
                    key={entry.key}
                    /*
                     * A recently used row gets its own id, because the same block
                     * is also listed in its own section below — two rows with one
                     * test id would make "this row exists once" unanswerable for
                     * every gate that counts the picker's rows.
                     */
                    testID={
                      section.key === 'recent'
                        ? `${testID}-recent-${entry.key}`
                        : `${testID}-row-${entry.key}`
                    }
                    onPress={() => {
                      if (!supported) {
                        Toast.show(
                          `${l(entry.label)} is coming soon`,
                          'circle-exclamation',
                        )
                        return
                      }
                      /*
                       * Remembered here rather than in either studio, so every
                       * caller — today's two, tomorrow's third — gets the shortcut
                       * without having to remember it, and so a block that is
                       * offered but refuses to be added is never remembered as
                       * used.
                       */
                      setRecent(rememberStudioBlock(entry.key))
                      onSelect(entry)
                    }}
                    style={ss.row}>
                    <View
                      style={[
                        ss.icon,
                        {
                          backgroundColor: entryColor + '22',
                          borderColor: entryColor + '44',
                        },
                      ]}>
                      <Feather
                        name={
                          entry.icon as ComponentProps<typeof Feather>['name']
                        }
                        size={17}
                        color={entryColor}
                      />
                    </View>
                    <View style={ss.rowText}>
                      <Text style={ss.rowLabel} numberOfLines={1}>
                        {l(entry.label)}
                      </Text>
                      <Text style={ss.rowDesc} numberOfLines={1}>
                        {supported ? l(entry.desc) : l`Coming soon`}
                      </Text>
                    </View>
                    {supported ? (
                      <Feather name="plus" size={16} color={c.textFaint} />
                    ) : (
                      <View style={ss.soon}>
                        <Text style={ss.soonTxt}>{l`Soon`}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                )
              })}
            </View>
          ))}
          {empty && (
            <View style={ss.empty} testID={`${testID}-empty`}>
              <Text style={ss.emptyTitle}>
                {l`No blocks match “${trimmed}”`}
              </Text>
              <Text style={ss.emptyBody}>
                {l`Try another word, or clear the search to browse every block.`}
              </Text>
            </View>
          )}
          {/*
            The closing line is about what to do with the block that was just
            added, so it has no place under a search that found none of them — there
            the empty state has said the only useful thing.
          */}
          {footnote && !empty && (
            <Text style={ss.footnote} testID={`${testID}-footnote`}>
              {footnote}
            </Text>
          )}
        </ScrollView>
      </View>
    </>
  )
}

const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    backdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: c.scrim,
      zIndex: 90,
    },
    sheet: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '88%',
      maxHeight: '88%',
      backgroundColor: c.surface,
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      borderTopWidth: 1,
      borderColor: c.borderStrong,
      zIndex: 100,
      overflow: 'hidden',
      shadowColor: c.shadow,
      shadowOpacity: 0.28,
      shadowRadius: 18,
      shadowOffset: {width: 0, height: -6},
      elevation: 12,
    },
    handle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      alignSelf: 'center',
      marginTop: 10,
      marginBottom: 10,
      backgroundColor: c.borderStrong,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 16,
      paddingBottom: 12,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    headerText: {flex: 1, minWidth: 0},
    title: {color: c.text, fontSize: 17, fontWeight: '800'},
    subtitle: {color: c.textMuted, fontSize: 12, marginTop: 2},
    close: {
      width: 30,
      height: 30,
      borderRadius: 15,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fill,
    },
    /*
     * The search field is a *field*, so it wears `fill` on `borderStrong` — the
     * same pair the rows and the studio's own inputs use, which is also what keeps
     * it under the appearance audit rather than a surface of its own.
     */
    search: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginHorizontal: 14,
      marginTop: 10,
      paddingHorizontal: 10,
      height: 40,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      color: c.text,
      fontSize: 13,
      /* RN-web gives an input its own vertical padding; the row already owns the
         height, so the field must not add a second one. */
      paddingVertical: 0,
    },
    searchClear: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fillStrong,
    },
    scroll: {
      paddingHorizontal: 14,
      paddingTop: 12,
      paddingBottom: 24,
      gap: 14,
    },
    group: {gap: 6},
    groupLabel: {
      color: c.textMuted,
      fontSize: 10.5,
      fontWeight: '800',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 10,
      paddingVertical: 9,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    icon: {
      width: 34,
      height: 34,
      borderRadius: 10,
      borderWidth: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowText: {flex: 1, minWidth: 0},
    rowLabel: {color: c.text, fontSize: 13, fontWeight: '700'},
    rowDesc: {color: c.textMuted, fontSize: 11, marginTop: 1},
    soon: {
      paddingHorizontal: 7,
      paddingVertical: 3,
      borderRadius: 7,
      backgroundColor: c.fillStrong,
    },
    soonTxt: {color: c.textMuted, fontSize: 9.5, fontWeight: '800'},
    /*
     * A search that found nothing has to say so in the sheet's own voice rather
     * than by rendering the sections it was asked to leave out — an empty sheet
     * reads as a sheet that failed to load.
     */
    empty: {gap: 6, paddingHorizontal: 24, paddingTop: 22},
    emptyTitle: {
      color: c.text,
      fontSize: 14,
      fontWeight: '700',
      textAlign: 'center',
    },
    emptyBody: {color: c.textMuted, fontSize: 12, textAlign: 'center'},
    footnote: {
      color: c.textFaint,
      fontSize: 11,
      textAlign: 'center',
      paddingHorizontal: 30,
    },
  })

/** The sheet's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
